import { createClient } from '@/lib/supabase/client'
import type { User as SupabaseUser, Session, AuthChangeEvent, Subscription } from '@supabase/supabase-js'
import type { User, UserRole } from '@/types/models/user.types'

// Auth 상태 변경 콜백 타입
export type AuthStateChangeCallback = (event: AuthChangeEvent, session: Session | null) => void

// 로그인 요청 타입
export interface SignInRequest {
  email: string
  password: string
}

// 회원가입 요청 타입
export interface SignUpRequest {
  email: string
  password: string
  full_name: string
  phone_number?: string
  address_id?: number | null
  role: UserRole
}

/**
 * 이메일/비밀번호로 로그인
 */
export async function signIn(credentials: SignInRequest): Promise<{ user: SupabaseUser; session: Session }> {
  try {
    const supabase = createClient()

    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    })

    if (error) {
      console.error('로그인 실패:', error)
      throw new Error(getAuthErrorMessage(error.message))
    }

    return { user: data.user, session: data.session }
  } catch (error) {
    console.error('signIn 함수 오류:', error)
    throw error
  }
}

/**
 * 회원가입
 */
export async function signUp(userData: SignUpRequest): Promise<{ user: SupabaseUser; session: Session | null }> {
  try {
    const supabase = createClient()

    // 0. 먼저 이메일 중복 체크 (public.users 테이블)
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('id')
      .eq('email', userData.email)
      .maybeSingle()

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('사용자 확인 실패:', checkError)
      throw new Error('사용자 확인 중 오류가 발생했습니다.')
    }

    if (existingUser) {
      throw new Error('이미 가입된 이메일입니다.')
    }

    // 1. auth.users에 사용자 생성
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          full_name: userData.full_name,
          role: userData.role
        }
      }
    })

    if (authError) {
      console.error('Auth 회원가입 실패:', authError)
      throw new Error(getAuthErrorMessage(authError.message))
    }

    if (!authData.user) {
      throw new Error('사용자 생성에 실패했습니다.')
    }

    // 이미 존재하는 사용자인 경우 (auth.users에 이미 있지만 확인되지 않은 경우)
    if (authData.user && !authData.session) {
      throw new Error('이미 가입된 이메일입니다.')
    }

    // 2. public.users에 프로필 정보 생성
    const { error: profileError } = await supabase
      .from('users')
      .insert({
        id: authData.user.id,
        email: userData.email,
        full_name: userData.full_name,
        phone_number: userData.phone_number || null,
        address_id: userData.address_id || null,
        role: userData.role
      })

    if (profileError) {
      console.error('프로필 생성 실패:', profileError)

      // 중복 키 에러인 경우
      if (profileError.code === '23505') {
        throw new Error('이미 가입된 이메일입니다.')
      }

      // Auth 사용자가 생성되었지만 프로필 생성 실패시
      throw new Error('프로필 정보 저장에 실패했습니다.')
    }

    return {
      user: authData.user,
      session: authData.session
    }
  } catch (error) {
    console.error('signUp 함수 오류:', error)
    throw error
  }
}

/**
 * 로그아웃
 * Supabase 세션 정리 + 대리접속 쿠키 정리
 */
export async function signOut(): Promise<void> {
  try {
    const supabase = createClient()

    // HttpOnly 쿠키와 서버 대행 상태를 인증 세션 종료 전에 폐기한다.
    const { clearImpersonationCookie } = await import('@/lib/auth/impersonate-cookie')
    try {
      await clearImpersonationCookie()
    } catch {
      console.warn('[Auth] 대행 상태 폐기 실패. 인증 로그아웃을 계속합니다.')
    }

    // 1. Supabase 로컬 세션 및 쿠키 삭제 (scope: 'local'로 서버 요청 없이 로컬만 정리)
    const { error } = await supabase.auth.signOut({ scope: 'local' })

    if (error) {
      console.warn('[Auth] Supabase local signOut failed (ignored):', error.message)
    }
  } catch (error) {
    console.error('[Auth] Logout error (continuing anyway):', error)
  }
}

/**
 * 현재 사용자 정보 조회
 *
 * @deprecated This function is deprecated. Use one of the following instead:
 * - Client Components: Use `useUserStore()` hook from '@/stores/user-store'
 * - Server Components: Use `getServerAuth()` from '@/lib/auth'
 * - Server Actions: Import `getCurrentUser` from '@/lib/actions/user-actions'
 *
 * Migration guide:
 * ```typescript
 * // Old (Client):
 * const user = await getCurrentUser()
 *
 * // New (Client Component - reactive):
 * import { useUserStore } from '@/stores/user-store'
 * const user = useUserStore(state => state.user)
 *
 * // New (Client non-reactive):
 * import { ClientAuth } from '@/stores/user-store'
 * const user = ClientAuth.getCurrentUser()
 *
 * // New (Server Component):
 * import { getServerAuth } from '@/lib/auth'
 * const auth = await getServerAuth()
 * const user = auth?.user
 * ```
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const supabase = createClient()

    const { data: { user }, error: authError } = await supabase.auth.getUser()

    if (authError || !user) {
      return null
    }

    // public.users에서 프로필 정보 조회
    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single()

    if (profileError) {
      console.error('프로필 조회 실패:', profileError)
      return null
    }

    return profile as User
  } catch (error) {
    console.error('getCurrentUser 함수 오류:', error)
    return null
  }
}

/**
 * 이메일 존재 여부 확인
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const supabase = createClient()

    const { count, error } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('email', email)

    if (error) {
      console.error('이메일 확인 실패:', error)
      return false
    }

    return (count ?? 0) > 0
  } catch (error) {
    console.error('checkEmailExists 함수 오류:', error)
    return false
  }
}

/**
 * 비밀번호 재설정 이메일 발송
 * 이메일이 존재하지 않으면 에러 발생
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    const supabase = createClient()

    // 먼저 이메일이 존재하는지 확인
    const emailExists = await checkEmailExists(email)
    if (!emailExists) {
      throw new Error('등록되지 않은 이메일입니다.')
    }

    // 환경변수 우선 사용, fallback으로 window.location.origin
    const baseUrl = process.env.NEXT_PUBLIC_APP_URL || (typeof window !== 'undefined' ? window.location.origin : '')

    if (!baseUrl) {
      throw new Error('앱 URL을 확인할 수 없습니다.')
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${baseUrl}/auth/reset-password`
    })

    if (error) {
      console.error('비밀번호 재설정 실패:', error)
      throw new Error(getAuthErrorMessage(error.message))
    }
  } catch (error) {
    console.error('resetPassword 함수 오류:', error)
    throw error
  }
}

/**
 * 비밀번호 업데이트 (재설정 완료)
 */
export async function updatePassword(newPassword: string): Promise<void> {
  try {
    const supabase = createClient()

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })

    if (error) {
      console.error('비밀번호 업데이트 실패:', error)
      const koreanMessage = getAuthErrorMessage(error.message)
      const errorCode = (error as { code?: string }).code || 'unknown'
      throw new Error(`${koreanMessage}\n\n[${errorCode}] ${error.message}`)
    }
  } catch (error) {
    console.error('updatePassword 함수 오류:', error)
    throw error
  }
}

/**
 * 현재 세션이 유효한지 확인
 * 비밀번호 재설정 페이지에서 사용
 */
export async function checkSession(): Promise<boolean> {
  try {
    const supabase = createClient()
    const { data: { session } } = await supabase.auth.getSession()
    return !!session
  } catch (error) {
    console.error('checkSession 함수 오류:', error)
    return false
  }
}

/**
 * Auth 상태 변경 구독
 * 비밀번호 재설정 페이지에서 PASSWORD_RECOVERY 이벤트 감지에 사용
 * @returns unsubscribe 함수를 포함한 Subscription 객체
 */
export function subscribeToAuthStateChange(callback: AuthStateChangeCallback): Subscription {
  const supabase = createClient()
  const { data: { subscription } } = supabase.auth.onAuthStateChange(callback)
  return subscription
}

/**
 * Auth 에러 메시지 한국어 변환
 */
function getAuthErrorMessage(errorMessage: string): string {
  const errorMap: Record<string, string> = {
    'Invalid login credentials': '이메일 또는 비밀번호가 올바르지 않습니다.',
    'Email not confirmed': '이메일 인증이 완료되지 않았습니다.',
    'User already registered': '이미 가입된 이메일입니다.',
    'Password should be at least 6 characters': '비밀번호는 최소 6자 이상이어야 합니다.',
    'Unable to validate email address: invalid format': '올바른 이메일 형식이 아닙니다.',
    'Signup is disabled': '회원가입이 비활성화되어 있습니다.',
    'Email rate limit exceeded': '이메일 전송 한도를 초과했습니다. 잠시 후 다시 시도해주세요.',
    'New password should be different from the old password.': '새 비밀번호는 이전 비밀번호와 달라야 합니다.'
  }

  return errorMap[errorMessage] || '오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
}

